import { useNavigate } from 'react-router-dom';
import type { LinkInput, SortColumn } from './types';
import Tag from '@WESCO-International/wdp-ui-components/components/tag/Tag';

export const kindOptions = [
  { label: 'Component', value: 'Component' },
  { label: 'API', value: 'API' },
];

export const lifecycleOptions = [
  { label: 'Proposed', value: 'Proposed' },
  { label: 'Catalog Aligned', value: 'Catalog Aligned' },
  { label: 'Ready to Use', value: 'Ready to Use' },
];

export const ownerOptions = [{ label: 'guest', value: 'guest' }];

export const componentTypeOptions = [
  { label: 'Service', value: 'Service' },
  { label: 'Website', value: 'Website' },
  { label: 'Library', value: 'Library' },
  { label: 'Documentation', value: 'Documentation' },
  { label: 'Tool', value: 'Tool' },
  { label: 'Other', value: 'Other' },
];

export const apiTypeOptions = [
  { label: 'OpenAPI', value: 'OpenAPI' },
  { label: 'GraphQL', value: 'GraphQL' },
  { label: 'AsyncAPI', value: 'AsyncAPI' },
  { label: 'GRPC', value: 'GRPC' },
];

export const domainOptions = [
  {
    label: 'AI',
    value: 'AI',
    owner: 'guest',
    description:
      'Models, pipelines, and platforms that power intelligent experiences.',
  },
  {
    label: 'IoT',
    value: 'IoT',
    owner: 'guest',
    description:
      'Edge connectivity, device management, and sensor data processing.',
  },
  {
    label: 'e-Commerce',
    value: 'e-Commerce',
    owner: 'guest',
    description:
      'Commerce capabilities covering catalog, orders, and customer journeys.',
  },
  {
    label: 'Products',
    value: 'Products',
    owner: 'guest',
    description:
      'Product lifecycle tools spanning CMP, WAM, and product data flows.',
  },
];

// Domain to system suggestions for cascading dropdowns; fall back to defaults when unknown.
export const domainSystemMap: Record<
  string,
  { label: string; value: string; owner?: string; description?: string }[]
> = {
  ai: [
    {
      label: 'Engineering Efficiency',
      value: 'Engineering Efficiency',
      owner: 'guest',
      description: 'Developer productivity accelerators and shared AI tooling.',
    },
    {
      label: 'BOAT Engine',
      value: 'BOAT Engine',
      owner: 'guest',
      description: 'Core orchestration engine powering AI workloads.',
    },
    {
      label: 'NLP',
      value: 'NLP',
      owner: 'guest',
      description:
        'Language models, embeddings, and text intelligence services.',
    },
    {
      label: 'Computer Vision',
      value: 'Computer Vision',
      owner: 'guest',
      description:
        'Imaging, detection, and recognition services for visual workloads.',
    },
    {
      label: 'Generative AI',
      value: 'Generative AI',
      owner: 'guest',
      description: 'Generation services for text, code, and media experiences.',
    },
    {
      label: 'Machine Learning',
      value: 'Machine Learning',
      owner: 'guest',
      description: 'Traditional ML model training, serving, and monitoring.',
    },
    {
      label: 'Deep Learning',
      value: 'Deep Learning',
      owner: 'guest',
      description: 'High-capacity neural models for complex perception tasks.',
    },
  ],
  iot: [
    {
      label: 'Connectivity',
      value: 'Connectivity',
      owner: 'guest',
      description:
        'Secure device onboarding, messaging, and connectivity services.',
    },
    {
      label: 'Alert Processing',
      value: 'Alert Processing',
      owner: 'guest',
      description:
        'Rules, anomaly detection, and alert routing for IoT signals.',
    },
    {
      label: 'Sensor Management',
      value: 'Sensor Management',
      owner: 'guest',
      description:
        'Inventory, calibration, and remote management of field sensors.',
    },
    {
      label: 'Smart Home Integration',
      value: 'Smart Home Integration',
      owner: 'guest',
      description: 'Smart home device interoperability and control surfaces.',
    },
  ],
  'e-Commerce': [
    {
      label: 'Inventory Management',
      value: 'Inventory Management',
      owner: 'guest',
      description: 'Inventory, availability, and stock movement services.',
    },
    {
      label: 'Data Analytics',
      value: 'Data Analytics',
      owner: 'guest',
      description:
        'Insights, dashboards, and analytics for commerce performance.',
    },
    {
      label: 'Order Management System',
      value: 'Order Management System',
      owner: 'guest',
      description: 'Order capture, routing, and fulfillment orchestration.',
    },
    {
      label: 'Customer Support',
      value: 'Customer Support',
      owner: 'guest',
      description: 'Case management and support experience tooling.',
    },
    {
      label: 'Order Tracking',
      value: 'Order Tracking',
      owner: 'guest',
      description: 'Shipment visibility and customer-facing tracking updates.',
    },
  ],
  products: [
    {
      label: 'CMP',
      value: 'CMP',
      owner: 'guest',
      description: 'Catalog management platform for product data governance.',
    },
    {
      label: 'WAM',
      value: 'WAM',
      owner: 'guest',
      description: 'Workflow and asset management for product operations.',
    },
  ],
};

export const getSystemOptionsForDomain = (domain?: string) => {
  const key = (domain ?? '').trim().toLowerCase();
  if (!key) return [];

  const normalizedMap = domainSystemMap as Record<
    string,
    { label: string; value: string; owner?: string; description?: string }[]
  >;
  // Prefer a lower-cased key match; fall back to the original key as-is.
  return normalizedMap[key] ?? normalizedMap[domain ?? ''] ?? [];
};

export const toArray = (s?: string): string[] =>
  (s ?? '')
    .split(',')
    .map(x => x.trim())
    .filter(Boolean);

export const NameFormatter = ({ row }: { row: any }) => {
  const navigate = useNavigate();
  return (
    <a
      href="#"
      onClick={e => {
        e.preventDefault();
        navigate(`/catalog-details/${encodeURIComponent(row.name)}`, {
          state: { item: row },
        });
      }}
      className="link"
    >
      {row.name}
    </a>
  );
};

// Render tags as WDP Tag components, showing up to two and collapsing the rest.
export const TagPills = ({ tags }: { tags: string[] }) => {
  const visible = tags.slice(0, 2);
  const extra = Math.max(0, tags.length - visible.length);

  if (!visible.length) return <span>-</span>;

  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'nowrap',
      }}
    >
      {visible.map(tag => (
        <Tag key={tag} label={tag} size="medium" variant="info" radius="xl" />
      ))}
      {extra > 0 && (
        <Tag
          key="more"
          variant="info"
          label={`+${extra}`}
          size="medium"
          radius="xl"
        />
      )}
    </div>
  );
};

export const buildCatalogColumns = () => [
  {
    key: 'name',
    name: 'Name',
    sortable: true,
    formatter: NameFormatter,
    width: 140,
  },
  { key: 'kind', name: 'Kind', sortable: true },
  { key: 'system', name: 'System' },
  { key: 'domain', name: 'Domain' },
  { key: 'owner', name: 'Owner' },
  { key: 'type', name: 'Type' },
  { key: 'lifecycle', name: 'Lifecycle' },
  { key: 'description', name: 'Description' },
  {
    key: 'tags',
    name: 'Tags',
    width: 180,
    formatter: ({ row }: { row: any }) => (
      <TagPills tags={parseTags(String(row.tags ?? ''))} />
    ),
  },
];

export const buildSubComponentColumns = () => [
  { key: 'name', name: 'Name', sortable: true, formatter: NameFormatter },
  { key: 'type', name: 'Type' },
  { key: 'owner', name: 'Owner' },
  { key: 'lifecycle', name: 'Lifecycle' },
  { key: 'system', name: 'System' },
  { key: 'domain', name: 'Domain' },
  { key: 'description', name: 'Description' },
];

export const buildLinks = (entity: any) => {
  const links = entity?.metadata?.links ?? entity?.links ?? [];
  if (!Array.isArray(links) || links.length === 0) return [];
  return links.map((l: any) => ({
    label: l?.title ?? l?.url ?? 'link',
    url: l?.url ?? '#',
  }));
};

export const buildApiSubRows = (entities: any[]) =>
  (entities ?? []).map((e: any) => ({
    name: e?.metadata?.name ?? '-',
    type: e?.spec?.type ?? e?.kind ?? 'Component',
    owner: e?.spec?.owner ?? '-',
    lifecycle: e?.spec?.lifecycle ?? '-',
    system: e?.spec?.system ?? '-',
    domain: e?.spec?.domain ?? '-',
    description: e?.metadata?.description ?? '-',
  }));

export const normalizeUrl = (url: string): string => {
  if (!url) return url;
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

export const normalizeTag = (tag: string): string =>
  (tag ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9+#-]+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);

export const capitalize = (s: string): string =>
  s ? `${s.charAt(0).toUpperCase()}${s.slice(1)}` : '';

export const parseTags = (raw: string): string[] =>
  raw
    .split(',')
    .map(t => normalizeTag(t))
    .filter(Boolean);

export const validateLinks = (
  links: LinkInput[],
): { valid: boolean; message?: string } => {
  for (let i = 0; i < links.length; i++) {
    const url = links[i].url.trim();
    const title = links[i].title.trim();

    if (!url && !title) continue;
    if (url && !title)
      return { valid: false, message: `Link ${i + 1}: add a title.` };
    if (title && !url)
      return { valid: false, message: `Link ${i + 1}: add a URL.` };

    const normalized = normalizeUrl(url);
    try {
      const parsed = new URL(normalized);
      const { hostname, protocol } = parsed;
      const isHttp = protocol === 'http:' || protocol === 'https:';
      const hostCharsOk = /^[a-z0-9.-]+$/i.test(hostname);
      const hasTld = /\.[a-z]{2,}$/i.test(hostname);

      if (!isHttp || !hostCharsOk || !hasTld) {
        return { valid: false, message: `Link ${i + 1}: enter a valid URL.` };
      }
    } catch {
      return { valid: false, message: `Link ${i + 1}: enter a valid URL.` };
    }
  }

  return { valid: true };
};

export const cleanLinks = (links: LinkInput[]): LinkInput[] =>
  links
    .map(l => ({ url: l.url.trim(), title: l.title.trim(), icon: l.icon }))
    .filter(l => l.url)
    .map(l => ({ ...l, url: normalizeUrl(l.url) }));

const compareValues = (a: unknown, b: unknown): number => {
  const av = a ?? '';
  const bv = b ?? '';
  if (typeof av === 'number' && typeof bv === 'number') return av - bv;
  const as = String(av).toLowerCase();
  const bs = String(bv).toLowerCase();
  if (as < bs) return -1;
  if (as > bs) return 1;
  return 0;
};

export const sortRows = <T extends Record<string, any>>(
  rows: T[],
  sortCols: SortColumn[],
): T[] => {
  if (!sortCols.length) return rows;
  return [...rows].sort((a, b) => {
    for (const sort of sortCols) {
      const result = compareValues(a[sort.columnKey], b[sort.columnKey]);
      if (result !== 0) return sort.direction === 'ASC' ? result : -result;
    }
    return 0;
  });
};

export const sleep = (ms: number): Promise<void> =>
  new Promise(resolve => window.setTimeout(resolve, ms));
